import type { TaskType } from '@legalzoom/constants';
import { useFetcher, useFormAction, useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';
import {
  getConsultationDescription,
  getTenantForConsultationType,
} from '../helpers';
import type { AppointmentServerResponse } from '../types/index';
import type {
  CancelAppointmentResponse,
  cancelAction,
} from '../routes/cancel.server';
import type { cancelActionWithForYou } from '../routes/cancelWithForYou.server';
import type {
  CreateAppointmentResponse,
  createAction,
} from '../routes/create.server';
import type { createActionWithForYou } from '../routes/createWithForYou.server';
import type {
  UpdateAppointmentResponse,
  updateAction,
} from '../routes/update.server';
import { ConsultationType } from '../types';

interface CreateConsultationProps {
  phoneNumber: string;
  taskType: TaskType;
  taskId: number;
  stateId: number;
  orderId: number;
  processingOrderId: number;
  advisorId: string;
  firmId: string;
  timezoneAbbr: string;
  date: string;
  topicId: string;
  serviceType: 'tax' | 'attorney';
}

interface CreateConsultationWithForYouProps {
  topicId: string;
  stateId: number;
  phoneNumber: string;
  advisorId: string;
  firmId: string;
  timezoneAbbr: string;
  date: string;
  forYouName: string;
  forYouId: string;
  orderId: number;
  accountId: string;
  description: string;
  serviceType: 'tax' | 'attorney';
}

export const useConsultations = ({
  onCancel,
  onCancelWithForYou,
  onCreate,
  onCreateWithForYou,
  onUpdate,
}: {
  onCancel?: (
    data: AppointmentServerResponse<CancelAppointmentResponse>,
  ) => void;
  onCancelWithForYou?: (
    data: AppointmentServerResponse<CancelAppointmentResponse>,
  ) => void;
  onCreate?: (
    data: AppointmentServerResponse<CreateAppointmentResponse>,
  ) => void;
  onCreateWithForYou?: (
    data: AppointmentServerResponse<CreateAppointmentResponse>,
  ) => void;
  onUpdate?: (
    data: AppointmentServerResponse<UpdateAppointmentResponse>,
  ) => void;
} = {}) => {
  const [searchParams] = useSearchParams();
  const serviceType = searchParams.get('serviceType');
  const tenantName = getTenantForConsultationType(
    serviceType === 'tax' ? ConsultationType.Tax : ConsultationType.Legal,
  ) as string;

  // Cancel
  const accountId = searchParams.get('accountId');
  const cancelFormAction = useFormAction(
    `/my/account/${accountId}/consultations/cancel`,
  );
  const {
    submit: submitCancel,
    state: cancelState,
    data: cancelData,
  } = useFetcher<typeof cancelAction>();
  const [awaitingCancel, setAwaitingCancel] = useState(false);
  useEffect(() => {
    if (
      awaitingCancel &&
      cancelState === 'idle' &&
      cancelData != null &&
      onCancel
    ) {
      onCancel(cancelData);
      setAwaitingCancel(false);
    }
  }, [cancelState, cancelData, awaitingCancel, setAwaitingCancel]);

  const cancelFormActionWithForYou = useFormAction(
    'consultations/cancelWithForYou',
  );
  const {
    submit: submitCancelWithForYou,
    state: cancelStateWithForYou,
    data: cancelDataWithForYou,
  } = useFetcher<typeof cancelActionWithForYou>();
  const [awaitingCancelWithForYou, setAwaitingCancelWithForYou] =
    useState(false);
  useEffect(() => {
    if (
      awaitingCancelWithForYou &&
      cancelStateWithForYou === 'idle' &&
      cancelDataWithForYou != null &&
      onCancelWithForYou
    ) {
      onCancelWithForYou(cancelDataWithForYou);
      setAwaitingCancelWithForYou(false);
    }
  }, [
    cancelStateWithForYou,
    cancelDataWithForYou,
    awaitingCancelWithForYou,
    setAwaitingCancelWithForYou,
  ]);

  // Create
  const createFormAction = useFormAction('consultations/create');
  const {
    submit: submitCreate,
    state: createState,
    data: createData,
  } = useFetcher<typeof createAction>();
  const [awaitingCreate, setAwaitingCreate] = useState(false);
  useEffect(() => {
    if (
      awaitingCreate &&
      createState === 'idle' &&
      createData != null &&
      onCreate
    ) {
      onCreate(createData);
      setAwaitingCreate(false);
    }
  }, [createState, createData, awaitingCreate, setAwaitingCreate]);

  const createFormActionWithForYou = useFormAction(
    'consultations/createWithForYou',
  );
  const {
    submit: submitCreateWithForYou,
    state: createStateWithForYou,
    data: createDataWithForYou,
  } = useFetcher<typeof createActionWithForYou>();
  const [awaitingCreateWithForYou, setAwaitingCreateWithForYou] =
    useState(false);
  useEffect(() => {
    if (
      awaitingCreateWithForYou &&
      createStateWithForYou === 'idle' &&
      createDataWithForYou != null &&
      onCreateWithForYou
    ) {
      onCreateWithForYou(createDataWithForYou);
      setAwaitingCreateWithForYou(false);
    }
  }, [
    createStateWithForYou,
    createDataWithForYou,
    awaitingCreateWithForYou,
    setAwaitingCreateWithForYou,
  ]);

  // Update
  const updateFormAction = useFormAction('consultations/update');
  const {
    submit: submitUpdate,
    state: updateState,
    data: updateData,
  } = useFetcher<typeof updateAction>();
  const [awaitingUpdate, setAwaitingUpdate] = useState(false);
  useEffect(() => {
    if (
      awaitingUpdate &&
      updateState === 'idle' &&
      updateData != null &&
      onUpdate
    ) {
      onUpdate(updateData);
      setAwaitingUpdate(false);
    }
  }, [updateState, updateData, awaitingUpdate, setAwaitingUpdate]);

  return {
    awaitingCancel,
    cancel({
      consultationId,
      confirmationNumber,
      accountId = null,
      consultationType = null,
      workItemId = null,
    }: {
      confirmationNumber: string;
      consultationId: string;
      accountId?: string | null;
      consultationType?: string | null;
      workItemId?: string | null;
    }) {
      setAwaitingCancel(true);
      return submitCancel(
        {
          consultationId,
          confirmationNumber,
          accountId,
          consultationType,
          workItemId,
        },
        {
          method: 'post',
          action: cancelFormAction,
        },
      );
    },
    awaitingCreate,
    create({
      phoneNumber,
      taskType,
      taskId,
      stateId,
      orderId,
      processingOrderId,
      advisorId,
      firmId,
      timezoneAbbr,
      date,
      topicId,
      serviceType = 'attorney',
    }: CreateConsultationProps) {
      setAwaitingCreate(true);
      return submitCreate(
        {
          phoneNumber,
          taskType,
          taskId: taskId.toString(),
          description: getConsultationDescription(taskType),
          stateId: stateId.toString(),
          orderId: orderId.toString(),
          processingOrderId: processingOrderId.toString(),
          advisorId,
          firmId,
          timezoneAbbr,
          date,
          topicId,
          serviceType,
        },
        {
          method: 'post',
          action: createFormAction,
        },
      );
    },
    awaitingCancelWithForYou,
    cancelWithForYou: (
      consultationId: string,
      confirmationNumber: string,
      accountId: string,
    ) => {
      setAwaitingCancelWithForYou(true);
      return submitCancelWithForYou(
        {
          consultationId,
          confirmationNumber,
          accountId,
        },
        {
          method: 'post',
          action: cancelFormActionWithForYou,
        },
      );
    },
    awaitingCreateWithForYou,
    createWithForYou: ({
      topicId,
      stateId,
      phoneNumber,
      advisorId,
      firmId,
      timezoneAbbr,
      date,
      forYouName,
      forYouId,
      orderId,
      accountId,
      description,
      serviceType = 'attorney',
    }: CreateConsultationWithForYouProps) => {
      setAwaitingCreateWithForYou(true);
      return submitCreateWithForYou(
        {
          topicId,
          stateId: stateId.toString(),
          phoneNumber,
          advisorId,
          firmId,
          timezoneAbbr,
          date,
          forYouName,
          forYouId,
          orderId: orderId.toString(),
          accountId,
          description,
          serviceType,
          tenantName,
        },
        { method: 'post', action: createFormActionWithForYou },
      );
    },
    awaitingUpdate,
    update({
      consultationId,
      advisorId,
      firmId,
      timezoneAbbr,
      date,
      phoneNumber,
      confirmationNumber,
      accountId,
      tenantName,
    }: {
      consultationId: string;
      advisorId: string;
      firmId: string;
      timezoneAbbr: string;
      date: string;
      phoneNumber: string;
      confirmationNumber: string;
      accountId: string;
      tenantName: string;
    }) {
      setAwaitingUpdate(true);
      return submitUpdate(
        {
          consultationId,
          advisorId,
          firmId,
          timezoneAbbr,
          date,
          phoneNumber,
          confirmationNumber,
          accountId,
          tenantName,
        },
        {
          method: 'post',
          action: updateFormAction,
        },
      );
    },
  };
};
